import {
	InteractionConfig,
	JourneyFilterState,
	JourneyMeta,
	JourneySimplify,
} from '../../../models/journey-explorer/journey-explorer';

export const MAX_GROUPS = 10;
export const MAX_GROUP_LABEL = 60;

export const EMPTY_SIMPLIFY: JourneySimplify = { hidden: [], groups: [] };

export const simplifyOf = (state: JourneyFilterState | null): JourneySimplify => state?.simplify ?? EMPTY_SIMPLIFY;

export const isSimplified = (simplify: JourneySimplify): boolean =>
	simplify.hidden.length > 0 || simplify.groups.length > 0;

export const isGroupLabel = (simplify: JourneySimplify, name: string): boolean =>
	simplify.groups.some((group) => group.label === name);

export const groupOf = (simplify: JourneySimplify, name: string): string | null =>
	simplify.groups.find((group) => group.names.includes(name))?.label ?? null;

export const viewName = (simplify: JourneySimplify, name: string): string | null =>
	simplify.hidden.includes(name) ? null : groupOf(simplify, name) ?? name;

const withSimplify = (state: JourneyFilterState, simplify: JourneySimplify): JourneyFilterState => ({
	...state,
	simplify: isSimplified(simplify) ? simplify : undefined,
});

export const toggleHidden = (state: JourneyFilterState, name: string): JourneyFilterState => {
	const simplify = simplifyOf(state);
	const hidden = simplify.hidden.includes(name)
		? simplify.hidden.filter((item) => item !== name)
		: [...simplify.hidden, name];
	return withSimplify(state, { ...simplify, hidden });
};

export const addGroup = (state: JourneyFilterState, label: string, names: string[]): JourneyFilterState => {
	const simplify = simplifyOf(state);
	return withSimplify(state, {
		hidden: simplify.hidden.filter((name) => !names.includes(name)),
		groups: [...simplify.groups, { label, names }].sort((a, b) => a.label.localeCompare(b.label)),
	});
};

export const removeGroup = (state: JourneyFilterState, label: string): JourneyFilterState => {
	const simplify = simplifyOf(state);
	return withSimplify(state, { ...simplify, groups: simplify.groups.filter((group) => group.label !== label) });
};

export const clearSimplify = (state: JourneyFilterState): JourneyFilterState => withSimplify(state, EMPTY_SIMPLIFY);

export const groupLabelError = (
	meta: JourneyMeta,
	simplify: JourneySimplify,
	label: string,
	names: string[]
): string | null => {
	const trimmed = label.trim();
	if (!trimmed) {
		return 'Give the group a name.';
	}
	if (trimmed.length > MAX_GROUP_LABEL) {
		return `Keep the name under ${MAX_GROUP_LABEL} characters.`;
	}
	const taken = meta.interaction_types.some((type) =>
		type.interactions.some((interaction) => interaction.name === trimmed)
	);
	if (taken || isGroupLabel(simplify, trimmed)) {
		return 'That name is already in use.';
	}
	if (names.length < 2) {
		return 'Pick at least two interactions.';
	}
	return null;
};

export const simplifyMeta = (meta: JourneyMeta, simplify: JourneySimplify): JourneyMeta => {
	if (!isSimplified(simplify)) {
		return meta;
	}
	const placed = new Set<string>();
	return {
		...meta,
		interaction_types: meta.interaction_types.map((type) => ({
			...type,
			interactions: type.interactions.flatMap((interaction): InteractionConfig[] => {
				const name = viewName(simplify, interaction.name);
				if (name === null || placed.has(name)) {
					return [];
				}
				placed.add(name);
				if (name === interaction.name) {
					return [interaction];
				}
				return [{ ...interaction, name, label: name, x: null, y: null }];
			}),
		})),
	};
};
